import React, { useContext } from "react";
import { DocumentLink } from "components/molecules/DocumentLink";
import { filter, map } from "rxjs/operators";
import { DocumentHeader } from "types/documents/DocumentHeader";
import { PDFViewerContext } from "contexts/PdfViewerContext";
import { useTrackPageEffect } from "hooks/services/useTrackPageEffect";
import { AuditEvent } from "types/track/AuditEventType";
import { Button, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { firstValueFrom } from "rxjs";
import documentService from "services/documentService";
import { useNotifyError } from "hooks/useNotifyError";
import { getPrimeLink } from "services/ssoService";

const SERVICE_URL = "api/members/documents";

const ExplanationOfBenefits = (): JSX.Element => {
  const { addAndOpenDocument } = useContext(PDFViewerContext);

  const {
    data: documents,
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useQuery({
    queryKey: ["eobdocuments"],
    select: (data: DocumentHeader[]) =>
      data.filter((doc) => doc.data?.document_type === "eob").map((doc) => ({
        ...doc,
        filename: `Part C ${doc.filename}`,
      })),
    queryFn: async () => {
      const documents = await firstValueFrom(
        documentService.getEobDocuments$().pipe(
          filter((response) => !!response?.response),
          map((response) => response.response.results)
        )
      );
      return documents;
    },
  });
  useNotifyError(documentsError, "Error loading documents");

  useTrackPageEffect({
    eventType: AuditEvent.MemberPortalViewExplanationOfBenefits,
  });

  const handleClickEOBStatements = () => {
    window.open(getPrimeLink("ExplanationOfBenefits"), "_blank");
  };

  const handleDocumentClick = (document: DocumentHeader) => {
    addAndOpenDocument({
      serviceUrl: SERVICE_URL,
      documentPath: document.id,
      displayName: document.filename || "",
    });
  };

  return (
    <Stack py={2} pb={6}>
      <Typography variant="h2" fontWeight="bold" component="h1" mb={4}>
        Explanation of Benefits (EOB)
      </Typography>
      <Stack spacing={2.5} mb={4} data-testid="eob-description">
        <Typography variant="body1">
          An explanation of benefits (EOB) is a monthly statement that explains
          what costs were paid for medical treatments you received and/or
          prescription drugs you purchased. Your Medicare Part C EOB provides
          information about medical claims, while your Medicare Part D EOB
          provides information about any prescriptions that were covered by
          Alignment Health Plan. The EOB is not a bill.
        </Typography>
        <Typography variant="body1">
          While not all communications are available for email delivery, we are
          working to provide you with more paperless options. If you have opted
          for email communications through your profile settings, this
          electronic EOB replaces the EOB sent to you by mail and you will be
          notified by email when a new electronic EOB is available.
        </Typography>
        <Typography variant="body1">
          If for any reason you want to change your preferences back to paper
          mail communications, you can select that option. Depending on the day
          of the month that you update your communication preference, there may
          be a slight delay processing your request due to print schedules.
        </Typography>
        <Typography variant="body1">
          This information is available for free in other languages. For
          assistance, please call your 24/7 ACCESS On-Demand Concierge at
          1-833-242-2223 (TTY 711).
        </Typography>
      </Stack>
      <Stack spacing={2} mb={2}>
        <Typography variant="h5" component="h2" fontWeight="bold">
        Medicare Part D Pharmacy EOB
        </Typography>
        <Stack justifyContent="flex-start" direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleClickEOBStatements} data-testid="view-part-d-eob-button">
            View Part D EOB Statements
          </Button>
        </Stack>
      </Stack>
      <Divider />
      <Stack mt={2} spacing={2}>
        <Typography variant="h5" component="h2" fontWeight="bold">
          Medicare Part C Medical and Hospital EOB
        </Typography>
        {isLoadingDocuments ? (
          <Stack spacing={2} data-testid="loading-documents">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton variant="rounded" width="100%" height={20} key={i} />
            ))}
          </Stack>
        ) : documents?.length ? (
          <Stack spacing={1} data-testid="documents">
            {(documents || []).map((document, i) => (
              <DocumentLink
                key={i}
                document={document}
                onDocumentClick={handleDocumentClick}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body1">
            No Documents Available
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export { ExplanationOfBenefits };

export default ExplanationOfBenefits;
